import React, { useEffect, useState, useCallback } from "react";
import { useLiveSync } from "@/lib/useLiveSync";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import {
  Bell, Flame, Shield, Receipt, Wrench, Car, PawPrint, ListChecks, ChevronRight,
} from "lucide-react";

const ICON = {
  bill: Flame, insurance: Shield, warranty: Receipt, maintenance: Wrench,
  vehicle: Car, pet: PawPrint, chore: ListChecks,
};
const SEV = {
  high: { ring: "ring-hearth-coral/30 bg-hearth-coral/8", chip: "bg-hearth-coral/15 text-hearth-coral", dot: "bg-hearth-coral" },
  medium: { ring: "ring-hearth-amber/30 bg-hearth-amber/8", chip: "bg-hearth-amber/20 text-[#7A5320]", dot: "bg-hearth-amber" },
  low: { ring: "ring-hearth-sage/25 bg-hearth-sage/5", chip: "bg-hearth-sage/15 text-hearth-sageDark", dot: "bg-hearth-sage" },
};

function Group({ label, list, eyebrow }) {
  if (list.length === 0) return null;
  return (
    <section className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-3xl text-hearth-ink">{label}</h2>
        <p className="caption">{eyebrow} · <span className="num text-hearth-ink">{list.length}</span></p>
      </div>
      <div className="space-y-2.5">
        {list.map((r, i) => {
          const Icon = ICON[r.kind] || Bell;
          const sev = SEV[r.severity] || SEV.medium;
          // Composite key disambiguates the case where a single vehicle
          // (same ref_id) has two reminders on the same date (e.g.
          // registration + insurance both due 2026-07-15) — subtype and
          // index tie-break to keep React keys unique.
          const key = `${r.kind}-${r.ref_id}-${r.date}-${r.subtype || r.label || i}`;
          return (
            <Link key={key} to={r.link} data-testid={`reminder-${r.kind}-${r.ref_id}`}
                  className="data-card p-4 fade-up flex items-center gap-4 hover:-translate-y-0.5 hover:shadow-sm transition">
              <div className={`w-10 h-10 rounded-lg ${sev.ring.replace("ring-", "bg-").split(" ")[0]} grid place-items-center shrink-0`}>
                <Icon size={17} strokeWidth={1.6} className="text-hearth-ink/80" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                  <p className="caption">{r.kind}</p>
                </div>
                <h3 className="text-hearth-ink font-medium truncate mt-0.5">{r.title}</h3>
                <p className="text-xs text-hearth-mute mt-0.5 truncate">{r.subtitle}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-sm text-hearth-ink">{r.date}</div>
                <div className={`chip mt-1 ${sev.chip}`}>
                  {r.days_until < 0 ? `${Math.abs(r.days_until)}d overdue` : r.days_until === 0 ? "today" : `in ${r.days_until}d`}
                </div>
              </div>
              <ChevronRight size={16} className="text-hearth-mute" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

const KINDS = ["all", "bill", "insurance", "warranty", "maintenance", "vehicle", "pet", "chore"];

export default function Reminders() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reminders", { params: { horizon_days: 90 } });
      setData(data);
    } catch (err) {
      console.error("Failed to load reminders:", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useLiveSync(load, 20000);

  if (!data) return <div className="text-hearth-mute font-display italic text-2xl">Looking ahead…</div>;

  const items = data.items.filter((i) => filter === "all" || i.kind === filter);
  const overdue = items.filter((i) => i.days_until < 0);
  const week = items.filter((i) => i.days_until >= 0 && i.days_until <= 7);
  const later = items.filter((i) => i.days_until > 7);

  return (
    <div>
      <PageHeader
        eyebrow="Looking ahead"
        title="Reminders"
        subtitle="What's coming up across the household — bills, renewals, vaccinations and chores."
      />

      <div className="flex flex-wrap gap-2 mb-8">
        {KINDS.map((k) => (
          <button key={k} data-testid={`reminder-filter-${k}`} onClick={() => setFilter(k)}
            className={`pill-btn capitalize ${filter === k ? "bg-hearth-ink text-white" : "bg-hearth-paper border border-hearth-line text-hearth-mute hover:text-hearth-ink"}`}>
            {k === "all" ? "All" : k}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Bell} title="No reminders." message="Add due dates to bills, renewals, vaccinations and warranties and they'll show up here." />
      ) : (
        <>
          <Group label="Overdue" list={overdue} eyebrow="past their date" />
          <Group label="This week" list={week} eyebrow="within 7 days" />
          <Group label="Coming up" list={later} eyebrow="next 90 days" />
        </>
      )}
    </div>
  );
}
